import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'

import { resolvePerencanaanReviewDiff } from 'app/KinerjaGuruPage/utils/perencanaan'
import { IndikatorPerencanaanPanel } from 'app/KinerjaShared/components/IndikatorPerencanaanPanel'
import { PerencanaanReviewDiff } from 'app/KinerjaShared/components/PerencanaanReviewDiff'

import type { PerencanaanItemReviewDiff } from 'app/KinerjaGuruPage/types/perencanaan'
import type { KinerjaUtamaDetail } from 'types/kinerja'

export type TugasTambahanItemProps = {
  originalIds: Array<number>
  reviewIds: Array<number>
  showReview: boolean
  contents: Array<KinerjaUtamaDetail>
  orderNumber: number
}

export function TugasTambahanItems({
  originalIds,
  reviewIds,
  showReview,
  contents,
  orderNumber,
}: TugasTambahanItemProps) {
  // Content as map for constant time lookup
  const contentsMap: Record<number, KinerjaUtamaDetail> = useMemo(() => {
    return contents.reduce((acc, content) => {
      acc[content.id] = content
      return acc
    }, {} as Record<number, KinerjaUtamaDetail>)
  }, [contents])

  // Resolved items
  const resolvedItems: Array<PerencanaanItemReviewDiff<string>> =
    useMemo(() => {
      let items: Array<PerencanaanItemReviewDiff<number>> = []

      if (showReview) {
        items = resolvePerencanaanReviewDiff(originalIds, reviewIds)
      } else {
        items = originalIds.map((id) => ({
          isChanged: false,
          original: id,
          review: null,
        }))
      }

      // Map to content
      const mappedItems: Array<PerencanaanItemReviewDiff<string>> = items.map(
        (item) => {
          const mappedItem = {
            original: null,
            review: null,
            isChanged: item.isChanged,
          }

          if (item.original) {
            mappedItem.original = contentsMap[item.original]?.rencanaHasilKerja
          }

          if (item.review) {
            mappedItem.review = contentsMap[item.review]?.rencanaHasilKerja
          }

          return mappedItem
        }
      )

      return mappedItems
    }, [originalIds, reviewIds, contentsMap, showReview])

  return (
    <>
      {resolvedItems.map((item, index) => {
        const resolvedOrderNumber = `${orderNumber}.${index + 1}`

        const hasOriginal = !!item.original
        const shouldShowReview = showReview && item.isChanged

        return (
          <IndikatorPerencanaanPanel
            key={`tugas-tambahan-${index}`}
            variant="static"
            orderNumber={resolvedOrderNumber}
            upperContent={
              shouldShowReview && (
                <PerencanaanReviewDiff
                  originalLabel="Perencanaan Guru"
                  reviewLabel="Penyesuaian dari Kepala Sekolah"
                  originalContent={
                    hasOriginal && (
                      <IndikatorPerencanaanPanel
                        variant="static"
                        orderNumber={resolvedOrderNumber}
                      >
                        <Text>
                          Terlaksananya tugas tambahan dalam mendukung
                          pembelajaran yang bermutu untuk semua berdasarkan
                          pilihan&nbsp;
                          <strong>{item.original}</strong>
                        </Text>
                      </IndikatorPerencanaanPanel>
                    )
                  }
                  reviewContent={
                    <IndikatorPerencanaanPanel
                      variant="static"
                      orderNumber={resolvedOrderNumber}
                    >
                      <Text>
                        Terlaksananya tugas tambahan dalam mendukung
                        pembelajaran yang bermutu untuk semua berdasarkan
                        pilihan&nbsp;
                        <strong>{item.review}</strong>
                      </Text>
                    </IndikatorPerencanaanPanel>
                  }
                />
              )
            }
          >
            <Text>
              Terlaksananya tugas tambahan dalam mendukung pembelajaran yang
              bermutu untuk semua berdasarkan pilihan&nbsp;
              <strong>{shouldShowReview ? item.review : item.original}</strong>
            </Text>
          </IndikatorPerencanaanPanel>
        )
      })}
    </>
  )
}
